import { FunctionComponent, Suspense } from 'react';
import { lazy } from 'react';
import { getDataSourceDisplayMemberValue } from './FieldEditor';

// Lazy import the Autocomplete and TextField components from MUI
const MUIAutocomplete = lazy(() => import('@mui/material/Autocomplete'));
const TextField = lazy(() => import('@mui/material/TextField'));

export interface MultiSelectProps {
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** The datasource for the multi-select. */
    dataSource?: { [index: string]: any }[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Is the input invalid? */
    isInvalid?: boolean;
    /** The value of the field. */
    value?: any;
    /** CSS class for the control. */
    className?: string;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: any) => void;
}

/**
 * Component: a wrapper around the MUI Autocomplete component in multi-select mode
 */
const MultiSelect: FunctionComponent<MultiSelectProps> = (props) => {
    const isEnabled = props.isEnabled ?? true;
    const dataSourceValueMember = props.dataSourceValueMember ?? 'value';
    const dataSourceDisplayMember = props.dataSourceDisplayMember ?? 'display';
    
    const acValue = props.value != null ? props.value : [];

    /** Render */
    return (
        <Suspense fallback={<div>Loading MultiSelect...</div>}>
            <MUIAutocomplete
                multiple
                limitTags={2}
                options={props.dataSource ?? []}
                getOptionLabel={(option) => getDataSourceDisplayMemberValue(option, dataSourceDisplayMember)}
                disabled={!isEnabled}
                value={acValue}
                onChange={(event, newValue) => { if (props.onValueChanged) props.onValueChanged(props.field, newValue); }}
                isOptionEqualToValue={(option: any, value: any) => {
                    const optionVal = option[dataSourceValueMember];
                    const valueVal = typeof value === 'object' ? value[dataSourceValueMember] : value;
                    return optionVal === valueVal;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required={props.isRequired}
                        error={props.isInvalid}
                        placeholder={props.placeholder}
                        variant="outlined"
                        className={props.className ?? "field-editor-input"}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            className: 'form-control'
                        }}
                    />
                )}
            />
        </Suspense>
    );
}

export default MultiSelect;